/**
 * How each repo's deploys stand (server/autoDeploy.ts), for the git panel's
 * header and the tmux view's git button. Module-level, like lib/gitPushes.ts:
 * the socket lives in the board store, the panel only while it is open.
 *
 * The socket's `deployStatus` events are not replayed, so whoever shows this
 * loads the lot on opening and again when the socket comes back.
 */
import { ref } from "vue";
import { api } from "./api.js";
import { toast } from "./toast.js";
import type { DeployStatus } from "@shared/deploy.js";

export const deployStatuses = ref(new Map<string, DeployStatus>());

/** Commands this tab queued behind a deploy (`runOrQueue`): how each went is
 *  said here, once, and not in every admin's tab. In sessionStorage, so the
 *  Reload a deploy of todos offers (lib/deployReload.ts) does not lose them. */
const QUEUED_KEY = "todos.queuedHere";
export const queuedHere = {
  read(): string[] {
    try {
      return JSON.parse(sessionStorage.getItem(QUEUED_KEY) ?? "[]") as string[];
    } catch {
      return [];
    }
  },
  write(ids: string[]): void {
    try {
      sessionStorage.setItem(QUEUED_KEY, JSON.stringify(ids));
    } catch {
      // Private window: the toast is lost; the command still runs.
    }
  },
  add(id: string): void {
    this.write([...this.read().filter((x) => x !== id), id]);
  },
  /** True if it was this tab's. */
  delete(id: string): boolean {
    const ids = this.read();
    if (!ids.includes(id)) return false;
    this.write(ids.filter((x) => x !== id));
    return true;
  },
};

function tellQueued(status: DeployStatus): void {
  const done = status.lastQueued;
  if (!done || !queuedHere.delete(done.id)) return;
  const what = `${done.label}${done.worktree ? ` (${done.worktree})` : ""}`;
  if (done.ok) toast(`${what}: done, after the deploy`);
  else if (done.session) toast(`${what} failed after the deploy: see ${done.session}`, 6000);
  else if (done.ran) toast(`${what} failed after the deploy${done.error ? `: ${done.error}` : ""}`, 8000);
  else toast(`${what} did not run after the deploy: ${done.error ?? "refused"}`, 6000);
}

export function deployStatusChanged(status: DeployStatus): void {
  const next = new Map(deployStatuses.value);
  next.set(status.repo, status);
  deployStatuses.value = next;
  tellQueued(status);
}

export async function loadDeployStatuses(): Promise<void> {
  try {
    const { deploys } = await api.get<{ deploys: DeployStatus[] }>("/api/tmux/deploys");
    deployStatuses.value = new Map(deploys.map((d) => [d.repo, d]));
    for (const d of deploys) tellQueued(d);
  } catch {
    // Not an admin, or the server is going: the header just says less.
  }
}

/** A deploy of `repo` is running, or about to. */
export const deployBusy = (repo: string | null): boolean => {
  const s = repo ? deployStatuses.value.get(repo) : undefined;
  return !!s && (!!s.running || s.queued);
};
